import React, { useState, useRef, useEffect } from 'react';
import type { FC, MouseEvent as ReactMouseEvent } from 'react';
import ReactDOM from 'react-dom';

import { Button, ButtonSize, ButtonType } from '../button';
import { EnvelopeIcon } from '../envelope-icon';
import { FloatingPanel, FloatingPanelItem, FloatingPanelType } from '../floating-panel';
import { HelpIcon } from '../help-icon';
import { CreatingTemplate } from './creating-template';
import { Frames } from './frames';
import { MultipleLayers } from './multiple-layers';
import { Organization } from './organization';
import { Overview } from './overview';
import { PublishTemplate } from './publish-template';
import { TextFields } from './text-fields';

enum QuickHelpPages {
    OVERVIEW = 'Overview of templates',
    CREATING_TEMPLATE = 'Creating a Template',
    FRAMES = 'Frames',
    TEXT_FIELDS = 'Text Fields',
    MULTIPLE_LAYERS = 'Adding multiple Layers',
    PUBLISH_TEMPLATE = 'Publish Template',
    ORGANIZATION = 'Organization',
}

export const QuickHelp: FC = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState<QuickHelpPages>(QuickHelpPages.OVERVIEW);
    const ref = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    const toggleMenu = (e: ReactMouseEvent) => {
        e.stopPropagation();
        setIsOpen((prev) => !prev);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getContentTile = () => {
        switch (currentPage) {
            case QuickHelpPages.OVERVIEW:
                return <Overview />;
            case QuickHelpPages.CREATING_TEMPLATE:
                return <CreatingTemplate />;
            case QuickHelpPages.FRAMES:
                return <Frames />;
            case QuickHelpPages.TEXT_FIELDS:
                return <TextFields />;
            case QuickHelpPages.MULTIPLE_LAYERS:
                return <MultipleLayers />;
            case QuickHelpPages.PUBLISH_TEMPLATE:
                return <PublishTemplate />;
            case QuickHelpPages.ORGANIZATION:
                return <Organization />;
            default:
                return <div />;
        }
    };

    const getPosition = () => {
        const menuRect = ref.current?.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const width = 900;
        const iconWidth = 100;

        let left = menuRect?.left ?? 0;
        const top = menuRect?.bottom ?? 0;

        if (left + width > windowWidth) {
            left = left - width;
        }
        return { top: top + 10, left: left + iconWidth };
    };

    return (
        <div className="relative inline-block" ref={ref}>
            <button type="button" onClick={toggleMenu}>
                <div className="flex flex-row gap-1">
                    <HelpIcon />
                    <p className="text-white text-sm font-normal">Quick Help</p>
                </div>
            </button>
            {isOpen &&
                ReactDOM.createPortal(
                    <div
                        className="z-[999] absolute w-[900px] h-2/3 bg-gray-150 flex flex-row shadow-xl rounded-md transition-all duration-100 ease-in-out overflow-y-scroll opacity-100 visible"
                        style={getPosition()}
                        ref={menuRef}
                    >
                        <FloatingPanel
                            className="px-4 h-full w-[250px] flex-shrink-0 overflow-y-auto"
                            omitSeperator={true}
                            type={FloatingPanelType.LIGHT}
                        >
                            <FloatingPanelItem
                                className="px-4 my-2 flex flex-col gap-2 items-start"
                                key={'quick-help-item'}
                            >
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.OVERVIEW)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.OVERVIEW ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Overview of templates
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.CREATING_TEMPLATE)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.CREATING_TEMPLATE ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Creating a Template
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.FRAMES)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.FRAMES ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Frames
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.TEXT_FIELDS)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.TEXT_FIELDS ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Text Fields
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.MULTIPLE_LAYERS)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.MULTIPLE_LAYERS ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Adding multiple Layers
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.PUBLISH_TEMPLATE)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.PUBLISH_TEMPLATE ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Publish Template
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage(QuickHelpPages.ORGANIZATION)}
                                    className={`hover:text-gray-10 text-base ${currentPage === QuickHelpPages.ORGANIZATION ? 'text-white' : 'text-gray-50'}`}
                                >
                                    Organization
                                </button>
                            </FloatingPanelItem>
                            <FloatingPanelItem className="px-4 my-2 h-full" key={'quick-help-content-space'}>
                                {[]}
                            </FloatingPanelItem>
                            <FloatingPanelItem className="px-4 my-2" key={'quick-help-content'}>
                                <div className="pr-4">
                                    <hr className="bg-gray-100 border-gray-100 w-full mb-4" />
                                </div>
                                <Button
                                    className="px-8"
                                    buttonType={ButtonType.SECONDARY}
                                    buttonSize={ButtonSize.LARGE}
                                    title="Reach Out"
                                    leading={<EnvelopeIcon />}
                                    onClick={() => {
                                        window.location.href = 'mailto:contact@frontmatter.io';
                                    }}
                                />
                            </FloatingPanelItem>
                        </FloatingPanel>
                        <div className="p-4 overflow-auto no-scrollbar">{getContentTile()}</div>
                    </div>,
                    document.body,
                )}
        </div>
    );
};
